import 'moment-duration-format';
import moment, { Moment } from 'moment-timezone';

export const zone = 'Asia/Bangkok';

export enum TIME_FORMAT {
  DD_MM_YYYY_HH_mm_ss = 'DD/MM/YYYY, HH:mm:ss',
  DD_MM_YYYY_HH_mm = 'DD/MM/YYYY HH:mm',
  DD_MM_YYYY = 'DD/MM/YYYY',
  HH = 'HH',
  HH_mm = 'HH:mm',
  HH_mm_ss = 'HH:mm:ss',
}

export function formatDateTime(
  time?: Date | string,
  format = TIME_FORMAT.DD_MM_YYYY_HH_mm_ss,
) {
  if (time) {
    const zone = 'Asia/Bangkok';
    return moment(time).tz(zone).format(format);
  }
  return 'No data';
}

export function formatFullDateTime(time?: Date | string) {
  if (time === 'No data') return 'No data';
  return formatDateTime(time, TIME_FORMAT.DD_MM_YYYY_HH_mm_ss);
}

export function formateDateToISOString(time: Date | string) {
  return moment(time, TIME_FORMAT.DD_MM_YYYY_HH_mm_ss).tz(zone).toISOString();
}

export function convertDateISOToMoment(date: string) {
  return moment(date, TIME_FORMAT.DD_MM_YYYY_HH_mm);
}

export function formatTime(time?: Date | string) {
  if (time === 'No data') return 'No data';
  return formatDateTime(time, TIME_FORMAT.HH_mm);
}

export function calculateEstimateTime(baseTimeTotal = 0): string {
  // baseTimeTotal minute
  const estimateTime = moment.utc(baseTimeTotal * 1000);

  if (estimateTime.second()) {
    estimateTime.add(1, 'minute').startOf('minute');
  }

  const estimateTimeHours = `${estimateTime.format('H [hrs]'.trim())}`;
  const estimateTimeMinute = `${estimateTime.format('m [mins]'.trim())}`;

  if (estimateTimeHours !== '0 hrs' && estimateTimeMinute !== '0 mins') {
    return `${estimateTimeHours} ${estimateTimeMinute}`;
  }

  if (estimateTimeHours !== '0 hrs') {
    return estimateTimeHours;
  }

  if (estimateTimeMinute !== '0 mins') {
    return estimateTimeMinute;
  }

  return 'No data';
}

export function getFormatTimeFromMilliseconds(milliseconds: number) {
  let timeFormatted = moment
    .duration(Math.abs(milliseconds))
    .format(TIME_FORMAT.HH_mm_ss);

  if (timeFormatted.split(':').length === 1) {
    timeFormatted = `00:00:${timeFormatted}`;
    return timeFormatted;
  }

  if (timeFormatted.split(':').length === 2) {
    timeFormatted = `00:${timeFormatted}`;
    return timeFormatted;
  }

  return timeFormatted;
}

export function getDiffTime(startTime: Date | string, endTime: Date | string) {
  const start = moment(startTime, TIME_FORMAT.DD_MM_YYYY_HH_mm_ss).tz(zone);
  const end = moment(endTime, TIME_FORMAT.DD_MM_YYYY_HH_mm_ss).tz(zone);
  return start.diff(end);
}

export function getCurrentTime(time: Date | string) {
  return moment(time).tz(zone).format(TIME_FORMAT.DD_MM_YYYY_HH_mm_ss);
}

export function getCurrentDate() {
  return moment().utc().toISOString();
}

export function isBefore(
  ref: string | Moment,
  compare: string | Moment,
  format = TIME_FORMAT.DD_MM_YYYY,
) {
  return moment(ref, format).isBefore(moment(compare, format));
}

export function isAfter(
  ref: string | Moment,
  compare: string | Moment,
  format = TIME_FORMAT.DD_MM_YYYY,
) {
  return moment(ref, format).isAfter(moment(compare, format));
}

export function disableCurrentDayAndBeforePickupDay(
  day: Moment,
  pickupDay: string,
) {
  const currentDay = getCurrentTime(new Date());

  const isPickupDayBeforeCurrentDay = isBefore(pickupDay, currentDay);
  const isShowDateBeforeCurrentDay = isBefore(day, currentDay);
  if (isPickupDayBeforeCurrentDay) return isShowDateBeforeCurrentDay;

  const isBeforePickupDay = isBefore(day, pickupDay);
  return isBeforePickupDay;
}

export function ONE_DAY_EXPIRE_DATE() {
  const date = new Date();
  return moment(date).utc().add(1, 'days').valueOf();
}

export function INFINITY_DATE() {
  return new Date('12/31/9999').getTime();
}
